import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { QueueItem, RegistrarStats } from '../mock/registrar.mock';
import { ApiResponse } from '../models/api-response.model';
import { environment } from '../../../environments/environment';
import { PaginatedResponse } from '../models/pagination.model';

/** Maps our internal tab keys to the labels the API expects (the UI tab names). */
const TAB_LABEL: Record<string, string> = {
  all: 'All',
  flagged: 'AI Flagged',
  complete: 'Complete',
  paid: 'Paid',
  sla: 'SLA Breach',
};

@Injectable({ providedIn: 'root' })
export class RegistrarService {
  private readonly base = `${environment.apiUrl}/registrar`;

  constructor(private http: HttpClient) {}

  /**
   * GET /api/registrar/queue?tab=<UI label>
   * The API filters server-side by tab and returns the full list (no paging).
   */
  getQueue(tab: string = 'all'): Observable<PaginatedResponse<QueueItem>> {
    const params = new HttpParams().set('tab', TAB_LABEL[tab] ?? 'All');
    return this.http
      .get<
        ApiResponse<PaginatedResponse<QueueItem>>
      >(`${this.base}/queue`, { params })
      .pipe(map((res) => res.data ?? []));
  }

  /** POST /api/registrar/open { caseFilingId } */
  open(caseFilingId: string): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${this.base}/open`, {
      caseFilingId,
    });
  }

  /** POST /api/registrar/approve { caseFilingId, note } */
  approve(
    caseFilingId: string,
    note: string,
  ): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${this.base}/approve`, {
      caseFilingId,
      note,
    });
  }

  /** POST /api/registrar/return { caseFilingId, note } */
  returnFiling(
    caseFilingId: string,
    note: string,
  ): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${this.base}/return`, {
      caseFilingId,
      note,
    });
  }

  /** POST /api/registrar/reject { caseFilingId, note } */
  reject(caseFilingId: string, note: string): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${this.base}/reject`, {
      caseFilingId,
      note,
    });
  }

  /** POST /api/registrar/escalate { caseFilingId, level, reason } */
  escalate(
    caseFilingId: string,
    level: number,
    reason: string,
  ): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${this.base}/escalate`, {
      caseFilingId,
      level,
      reason,
    });
  }

  /** POST /api/registrar/override { caseFilingId, fieldName, originalValue, correctedValue, justification } */
  override(
    caseFilingId: string,
    fieldName: string,
    originalValue: string,
    correctedValue: string,
    justification: string,
  ): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${this.base}/override`, {
      caseFilingId,
      fieldName,
      originalValue,
      correctedValue,
      justification,
    });
  }

  /** GET /api/registrar/analytics/charts — statistics page */
  getStats(): Observable<RegistrarStats> {
    return this.http
      .get<ApiResponse<RegistrarStats>>(`${this.base}/analytics/charts`)
      .pipe(map((res) => res.data));
  }
}
